import React, { useCallback, useEffect, useState } from 'react'
import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { documentService } from '../../services/document.service'
import { documentWorkflowDefinitionService } from '../../services/document-workflow-definition.service'
import { documentWorkflowHistoryService } from '../../services/document-workflow-history.service'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { WorkflowStatus } from '../../types'
import type { DocumentWorkflowDefinitionStep } from '../../services/document-workflow-definition.service'
import { DocumentWorkflowStepper, WorkflowStepInfo, WorkflowHistoryEntry } from './DocumentWorkflowStepper'

export interface DocumentWorkflowStepperModalProps {
  show: boolean
  onHide: () => void
  documentId: number
  workflowStatuses: WorkflowStatus[]
  /** Document's current workflow status id (from parent); used when API omits it e.g. for completed workflow. */
  workflowStatusId?: number | null
  /** When set, clicking an action opens comment form; on confirm this is called with actionId and comment. */
  onWorkflowAction?: (actionId: number, comment?: string) => void
  userCanEdit?: boolean
  /** When set, modal opens with this action pre-selected (comment form shown immediately). */
  initialActionId?: number | null
  /** Called when modal has applied initialActionId so parent can clear it. */
  onInitialActionConsumed?: () => void
}

interface WorkflowModalData {
  workflowName: string
  steps: WorkflowStepInfo[]
  currentStepPosition: number | null
  availableActions: Array<{ id: number; name: string; end_workflow: boolean; color?: string; text_color?: string; comment_required?: boolean }>
  userCanPerformActions: boolean
  workflowHistory: WorkflowHistoryEntry[]
  finalOutcomeStatusId: number | null
  workflowDefinitionId: number | null
  stepDefinitions?: DocumentWorkflowDefinitionStep[]
  currentStepAssigneeUsernames?: string[]
  currentStepAssigneeGroupNames?: string[]
}

interface ApiWorkflowStep {
  id: number
  position: number
  status_id: number
  name?: string
}

export const DocumentWorkflowStepperModal: React.FC<DocumentWorkflowStepperModalProps> = ({
  show,
  onHide,
  documentId,
  workflowStatuses,
  workflowStatusId: workflowStatusIdProp,
  onWorkflowAction,
  userCanEdit = true,
  initialActionId,
  onInitialActionConsumed,
}) => {
  const { t } = useTranslation()
  const [dataLoading, setDataLoading] = useState(false)
  const [workflowData, setWorkflowData] = useState<WorkflowModalData | null>(null)
  const [pendingAction, setPendingAction] = useState<{ id: number; name: string; comment_required?: boolean } | null>(null)
  const [comment, setComment] = useState('')
  const [commentError, setCommentError] = useState(false)

  const loadData = useCallback(async () => {
    if (!documentId) return
    setDataLoading(true)
    setWorkflowData(null)
    try {
      const [actionsData, history] = await Promise.all([
        documentService.getWorkflowAvailableActions(documentId),
        documentWorkflowHistoryService.getWorkflowHistory(documentId),
      ])

      const workflowStatusIdFromApi = actionsData.status_id ?? workflowStatusIdProp ?? null

      const normalizeSteps = (rawSteps: ApiWorkflowStep[] | undefined): WorkflowStepInfo[] => (
        (rawSteps ?? []).map((s) => ({
          id: s.id,
          position: s.position,
          workflow_status_id: s.status_id,
          name: s.name,
        }))
      )

      const canFetchDefinition = permissionsService.currentUserCan(
        PermissionAction.View,
        PermissionType.DocumentWorkflowTask,
      ) || permissionsService.currentUserCan(
        PermissionAction.View,
        PermissionType.DocumentWorkflowDefinition,
      )

      if (actionsData.in_workflow && actionsData.workflow_definition) {
        let steps: WorkflowStepInfo[] = normalizeSteps(actionsData.steps)
        let stepDefinitions: DocumentWorkflowDefinitionStep[] | undefined
        if (canFetchDefinition) {
          const def = await documentWorkflowDefinitionService.get(actionsData.workflow_definition.id)
          stepDefinitions = (def.steps ?? []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          if (steps.length === 0) {
            steps = stepDefinitions.map((s) => ({
              id: s.id!,
              position: s.position,
              workflow_status_id: s.workflow_status_id,
              name: s.name,
            }))
          }
        }
        setWorkflowData({
          workflowName: actionsData.workflow_definition.name,
          steps,
          currentStepPosition: actionsData.current_step?.position ?? null,
          availableActions: actionsData.available_actions ?? [],
          userCanPerformActions: actionsData.user_can_perform_actions ?? false,
          workflowHistory: history,
          finalOutcomeStatusId: null,
          workflowDefinitionId: actionsData.workflow_definition.id,
          stepDefinitions,
          currentStepAssigneeUsernames: actionsData.current_step_assignee_usernames,
          currentStepAssigneeGroupNames: actionsData.current_step_assignee_group_names,
        })
        return
      }

      const workflowStatusId = workflowStatusIdFromApi
      if (!workflowStatusId || !history.length) {
        setWorkflowData(null)
        return
      }
      let completedWorkflowName: string | null = actionsData.workflow_definition?.name ?? null
      let completedSteps: WorkflowStepInfo[] = normalizeSteps(actionsData.steps)
      let completedWorkflowDefinitionId: number | null = actionsData.workflow_definition?.id ?? null
      let completedStepDefinitions: DocumentWorkflowDefinitionStep[] | undefined

      if (completedSteps.length === 0) {
        const statusChangeEntry = history.find(
          (e) => e.entry_type === 'status_change' && e.workflow_definition_id != null
        )
        if (!statusChangeEntry?.workflow_definition_id) {
          setWorkflowData(null)
          return
        }
        completedWorkflowDefinitionId = statusChangeEntry.workflow_definition_id
        /** History payload already carries the name; admin GET is only needed for the step layout. */
        completedWorkflowName = statusChangeEntry.workflow_definition_name ?? completedWorkflowName
        if (canFetchDefinition) {
          const def = await documentWorkflowDefinitionService.get(statusChangeEntry.workflow_definition_id)
          completedWorkflowName = def.name
          completedStepDefinitions = (def.steps ?? []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
          completedSteps = completedStepDefinitions.map((s) => ({
            id: s.id!,
            position: s.position,
            workflow_status_id: s.workflow_status_id,
            name: s.name,
          }))
        }
      } else if (canFetchDefinition && completedWorkflowDefinitionId != null) {
        try {
          const def = await documentWorkflowDefinitionService.get(completedWorkflowDefinitionId)
          completedStepDefinitions = (def.steps ?? []).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        } catch {
          completedStepDefinitions = undefined
        }
      }

      if (!completedWorkflowName || completedSteps.length === 0) {
        setWorkflowData(null)
        return
      }

      setWorkflowData({
        workflowName: completedWorkflowName,
        steps: completedSteps,
        currentStepPosition: null,
        availableActions: [],
        userCanPerformActions: false,
        workflowHistory: history,
        finalOutcomeStatusId: workflowStatusId,
        workflowDefinitionId: completedWorkflowDefinitionId,
        stepDefinitions: completedStepDefinitions,
      })
    } catch {
      setWorkflowData(null)
    } finally {
      setDataLoading(false)
    }
  }, [documentId, workflowStatusIdProp])

  useEffect(() => {
    if (show && documentId) {
      setComment('')
      setCommentError(false)
      loadData()
    } else {
      setWorkflowData(null)
      setPendingAction(null)
      setComment('')
      setCommentError(false)
    }
  }, [show, documentId, loadData])

  // When opened with initialActionId (e.g. from inline action button), preselect that action so comment form shows
  useEffect(() => {
    if (!workflowData || initialActionId == null) return
    const action = workflowData.availableActions.find((a) => a.id === initialActionId)
    if (action) {
      setComment('')
      setCommentError(false)
      setPendingAction({ id: action.id, name: action.name, comment_required: action.comment_required })
      onInitialActionConsumed?.()
    }
  }, [workflowData, initialActionId, onInitialActionConsumed])

  const handleActionClick = (actionId: number) => {
    const action = workflowData?.availableActions.find((a) => a.id === actionId)
    setPendingAction(
      action
        ? { id: action.id, name: action.name, comment_required: action.comment_required }
        : { id: actionId, name: '' },
    )
    setComment('')
    setCommentError(false)
  }

  const handleCommentCancel = () => {
    setPendingAction(null)
    setComment('')
    setCommentError(false)
  }

  const handleCommentConfirm = () => {
    if (!pendingAction) return
    if (pendingAction.comment_required && !comment.trim()) {
      setCommentError(true)
      return
    }
    onWorkflowAction?.(pendingAction.id, comment.trim() || undefined)
    setPendingAction(null)
    setComment('')
    setCommentError(false)
    onHide()
  }

  const handleModalHide = () => {
    if (pendingAction) {
      setPendingAction(null)
      setComment('')
      setCommentError(false)
    }
    onHide()
  }

  const showCommentForm = pendingAction != null && onWorkflowAction != null
  const commentRequired = pendingAction?.comment_required ?? false
  const canConfirmComment = !commentRequired || comment.trim().length > 0

  return (
    <Modal show={show} onHide={handleModalHide} size="lg" centered>
      <Modal.Header className="d-flex align-items-center" closeButton={false}>
        <Modal.Title className="mb-0 me-2 flex-grow-1 text-break">
          {showCommentForm
            ? t('document_workflow.action_comment_title', { action: pendingAction?.name })
            : t('document_workflow.title')}
        </Modal.Title>
        <Button
          variant="outline-secondary"
          size="sm"
          className="ms-auto flex-shrink-0 related-items-modal-close"
          onClick={handleModalHide}
          aria-label={t('close')}
        >
          <i className="bi bi-x-lg" aria-hidden />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {dataLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2 text-muted mb-0">{t('loading_data')}</p>
          </div>
        ) : showCommentForm ? (
          <Form>
            {commentRequired && (
              <p className="text-muted small mb-3">{t('document_workflow.action_comment_required_help')}</p>
            )}
            <Form.Group className="mb-3">
              <Form.Label>
                {commentRequired ? t('document_workflow.comment_required_label') : t('document_workflow.comment')}
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                autoFocus
                value={comment}
                onChange={(e) => {
                  setComment(e.target.value)
                  if (commentError && e.target.value.trim()) {
                    setCommentError(false)
                  }
                }}
                placeholder={
                  commentRequired
                    ? t('document_workflow.comment_required_placeholder')
                    : t('document_workflow.comment_placeholder')
                }
                isInvalid={commentError}
              />
              {commentError && (
                <Form.Control.Feedback type="invalid">
                  {t('document_workflow.comment_required_error')}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="outline-secondary" onClick={handleCommentCancel}>
                {t('cancel')}
              </Button>
              <Button variant="primary" onClick={handleCommentConfirm} disabled={!canConfirmComment}>
                {t('document_workflow.run')}
              </Button>
            </div>
          </Form>
        ) : workflowData ? (
          <>
            <DocumentWorkflowStepper
              workflowName={workflowData.workflowName}
              steps={workflowData.steps}
              currentStepPosition={workflowData.currentStepPosition}
              workflowStatuses={workflowStatuses}
              availableActions={workflowData.availableActions}
              userCanPerformActions={workflowData.userCanPerformActions}
              onActionClick={onWorkflowAction ? handleActionClick : undefined}
              userCanEdit={userCanEdit}
              workflowHistory={workflowData.workflowHistory}
              currentWorkflowDefinitionId={workflowData.workflowDefinitionId}
              stepDefinitions={workflowData.stepDefinitions}
              closeStepEntry={
                workflowData.finalOutcomeStatusId != null && workflowData.workflowHistory.length > 0
                  ? documentWorkflowHistoryService.getLatestEntryForStep(
                      workflowData.workflowHistory,
                      workflowData.finalOutcomeStatusId
                    )
                  : null
              }
              currentStepAssigneeUsernames={workflowData.currentStepAssigneeUsernames}
              currentStepAssigneeGroupNames={workflowData.currentStepAssigneeGroupNames}
            />
          </>
        ) : (
          <p className="text-muted text-center py-4 mb-0">
            {t('document_workflow.no_workflow_data')}
          </p>
        )}
      </Modal.Body>
    </Modal>
  )
}
